<script setup>
import { computed } from 'vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import FormTextarea from '@/components/Atoms/FormTextarea.vue'
import SiteRichTextEditor from '@/components/Sites/SiteRichTextEditor.vue'
import SiteTimeZoneSelect from '@/components/Sites/SiteTimeZoneSelect.vue'
import BroadcastServiceTimesEditor from './BroadcastServiceTimesEditor.vue'
import { audienceLabels, AUDIENCE_CHURCH } from '@/components/Sites/broadcastAudience'

// Settings › Broadcast › Service info: the runtime settings that fill the
// broadcast home page around the player (sites-broadcast-template-contract.md
// §2.2). The page owns the form object and the PATCH; this card edits it.
const props = defineProps({
	/** Live content items of the site (`GET /content?type=live`) for the main-stream picker. */
	liveItems: {
		type: Array,
		default: () => [],
	},
	categories: {
		type: Array,
		default: () => [],
	},
	/** `{ [key]: message }` plus `serviceTimes: string[]` per row. */
	errors: {
		type: Object,
		default: () => ({}),
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	/** True while the zone shown is the browser's guess (nothing saved on the runtime yet). */
	timezoneSuggested: {
		type: Boolean,
		default: false,
	},
	/** Keys the runtime accepts (`writableKeys`); null = unknown → everything shown. */
	writableKeys: {
		type: Array,
		default: null,
	},
	/** Broadcast audience preset (`broadcastAudience`): card wording follows it. */
	audience: {
		type: String,
		default: AUDIENCE_CHURCH,
	},
})
const labels = computed(() => audienceLabels(props.audience))
const form = defineModel({ type: Object, required: true })

const set = (key, value) => { form.value = { ...form.value, [key]: value } }
const writable = (key) => !Array.isArray(props.writableKeys) || props.writableKeys.includes(key)

const liveOptions = computed(() => [
	{ value: 0, text: props.liveItems.length ? 'Automatic (first live item)' : 'Automatic (first live item) — no live items yet' },
	...props.liveItems.map((item) => ({ value: Number(item.id), text: `${item.title}${item.published === false ? ' (unpublished)' : ''}` })),
])
const categoryOptions = computed(() => [
	{ value: 0, text: 'All recordings' },
	...props.categories.map((c) => ({ value: Number(c.id), text: c.name })),
])
// Broadcast sites have no category nav, so most have no categories: the row
// then simply shows all recordings. A saved category stays editable.
const showCategoryPicker = computed(() => props.categories.length > 0 || Number(form.value.broadcastRecentServicesCategoryId) > 0)
const taglineLeft = computed(() => 140 - String(form.value.broadcastTagline || '').length)
</script>

<template>
	<section class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="broadcast-service-info">
		<h3 class="mb-1 text-sm font-semibold text-white" data-testid="broadcast-info-title">{{ labels.infoTitle }}</h3>
		<p class="mb-4 text-tiny text-surface-7">{{ labels.infoDescription }}</p>

		<div class="flex flex-col gap-4">
			<div class="min-w-0">
				<FormInput id="broadcast-tagline" :model-value="form.broadcastTagline" label="Tagline" placeholder="e.g. Sundays 9:00 & 11:00 AM · Wednesdays 7:00 PM" input-classes="placeholder:text-surface-6" :maxlength="140" :disabled="disabled" :is-error="Boolean(errors.broadcastTagline)" class="min-w-0" @update:model-value="set('broadcastTagline', $event)" />
				<p class="mb-0 mt-1.5 text-tiny" :class="errors.broadcastTagline ? 'text-error' : 'text-surface-7'">{{ errors.broadcastTagline || `Shown under your name in the header. ${taglineLeft} characters left.` }}</p>
			</div>

			<div class="min-w-0">
				<SiteTimeZoneSelect id="broadcast-timezone" :model-value="form.broadcastTimezone" label="Time zone" :disabled="disabled" class="min-w-0" @update:model-value="set('broadcastTimezone', $event)" />
				<p class="mb-0 mt-1.5 text-tiny text-surface-7" data-testid="broadcast-timezone-hint">
					{{ timezoneSuggested ? `Your browser’s time zone, saved with your first ${labels.row} — ${labels.tzSuggested}. ` : '' }}{{ labels.timesHint }}
				</p>
			</div>

			<div class="min-w-0">
				<p class="mb-1 text-tiny font-medium text-surface-text-1" data-testid="broadcast-times-label">{{ labels.times }}</p>
				<BroadcastServiceTimesEditor :model-value="form.broadcastServiceTimes" :audience="audience" :errors="errors.serviceTimes || []" :disabled="disabled" @update:model-value="set('broadcastServiceTimes', $event)" />
				<p v-if="errors.broadcastServiceTimes" class="mb-0 mt-1.5 text-tiny text-error" data-testid="service-times-error">{{ errors.broadcastServiceTimes }}</p>
			</div>

			<div class="min-w-0" data-testid="broadcast-about">
				<SiteRichTextEditor id="broadcast-about" :model-value="form.broadcastAbout" label="About" placeholder="Who you are, what to expect, how to get involved…" hint="Headings, lists and links are kept; everything else is cleaned up when saved." @update:model-value="set('broadcastAbout', $event)" />
				<p v-if="errors.broadcastAbout" class="mb-0 mt-1.5 text-tiny text-error">{{ errors.broadcastAbout }}</p>
			</div>

			<div class="min-w-0">
				<FormTextarea id="broadcast-address" :model-value="form.broadcastAddress" label="Address" placeholder="1200 Oak Street&#10;Springfield, IL 62704" :rows="2" :max-rows="4" :maxlength="300" :disabled="disabled" :is-error="Boolean(errors.broadcastAddress)" hint="Shown in the About block with a Directions link." @update:model-value="set('broadcastAddress', $event)" />
				<p v-if="errors.broadcastAddress" class="mb-0 mt-1.5 text-tiny text-error">{{ errors.broadcastAddress }}</p>
			</div>

			<div class="min-w-0">
				<FormTextarea id="broadcast-giving-message" :model-value="form.broadcastGivingMessage" label="Giving message" placeholder="Your generosity keeps our doors open and our ministries growing." :rows="2" :max-rows="4" :maxlength="500" :disabled="disabled" :is-error="Boolean(errors.broadcastGivingMessage)" hint="A sentence next to the Give link in the About block and at the top of the giving form." @update:model-value="set('broadcastGivingMessage', $event)" />
				<p v-if="errors.broadcastGivingMessage" class="mb-0 mt-1.5 text-tiny text-error">{{ errors.broadcastGivingMessage }}</p>
			</div>

			<div v-if="writable('broadcastLiveItemId')" class="min-w-0">
				<FormSelect id="broadcast-live-item" :model-value="form.broadcastLiveItemId" label="Main live stream" :options="liveOptions" :disabled="disabled" class="min-w-0" @update:model-value="set('broadcastLiveItemId', Number($event))" />
				<p class="mb-0 mt-1.5 text-tiny" :class="errors.broadcastLiveItemId ? 'text-error' : 'text-surface-7'">
					{{ errors.broadcastLiveItemId || (liveItems.length ? 'The stream in the big player. Automatic picks the first published live item in your Library.' : 'Add a live stream under Content › Library to pick it here; Automatic uses the first one you add.') }}
				</p>
			</div>

			<div class="rounded-lg bg-surface-3 px-3 py-2.5" data-testid="broadcast-recent-services">
				<FormSwitch id="broadcast-recent-services" :model-value="form.broadcastRecentServicesEnabled !== false" :label="labels.recent" label-position="left" label-styles="w-full justify-between gap-3 text-sm text-surface-text-1" :disabled="disabled" @update:model-value="set('broadcastRecentServicesEnabled', $event)" />
				<p class="mb-0 mt-0.5 pr-14 text-tiny text-surface-7">The newest recordings under the player, with a link to all of them.</p>
				<div v-if="form.broadcastRecentServicesEnabled !== false && showCategoryPicker" class="nested-field mt-3 min-w-0" data-testid="broadcast-recent-category-field">
					<FormSelect id="broadcast-recent-category" :model-value="form.broadcastRecentServicesCategoryId" label="Take recordings from" :options="categoryOptions" :disabled="disabled" class="min-w-0" @update:model-value="set('broadcastRecentServicesCategoryId', Number($event))" />
					<p class="mb-0 mt-1.5 text-tiny" :class="errors.broadcastRecentServicesCategoryId ? 'text-error' : 'text-surface-7'" data-testid="broadcast-recent-category-hint">{{ errors.broadcastRecentServicesCategoryId || labels.recentHint }}</p>
				</div>
			</div>
		</div>
	</section>
</template>

<style scoped>
/* A filled field inside a surface-3 box needs a border to read as one (same as the service-time rows). */
.nested-field :deep(select) {
	border: 1px solid var(--surface-5);
}
.nested-field :deep(select:hover:not(:disabled)) {
	border-color: var(--surface-6);
}
.nested-field :deep(select:focus) {
	border-color: var(--c-main);
	box-shadow: 0 0 0 1px var(--c-main);
}
</style>
